import type { PaginatedResult } from '@api/resources/resource';
import { defineStore } from 'pinia';

import type {
  Conversation,
  Dispute,
  DisputeReason,
  SuggestedDisputeReason,
  Task,
} from '@stuut/api/models';
import type { HttpError } from '@stuut/common/src/api_client';

import { stuut } from '@/api';
import { useProfile } from '@/store';

interface ResourceArgs {
  force?: boolean;
  params?: Record<string, any>;
}

interface AsyncResource<T> {
  data: T | undefined;
  error: Error | null;
  loadedAt: number | null;
  loading: boolean;
  request: Promise<T> | undefined;
}

interface State {
  disputeConversationsResource: AsyncResource<PaginatedResult<Conversation>>;
  disputeReasonResource: AsyncResource<DisputeReason[]>;
  disputeResource: AsyncResource<Dispute>;
  disputes: Dispute[];
  disputeTasksResource: AsyncResource<Task[]>;
  suggestedReasonResource: AsyncResource<SuggestedDisputeReason[]>;
}

function createResource<T>(): AsyncResource<T> {
  return {
    data: undefined,
    error: null,
    loadedAt: null,
    loading: false,
    request: undefined,
  };
}

export const useDisputeStore = defineStore('disputes', {
  actions: {
    addReason(reason) {
      if (!reason?.id) return;

      const currentReasons = this.disputeReasonResource.data || [];
      const updatedReasons = [...currentReasons, reason];

      this.$patch({
        disputeReasonResource: {
          ...this.disputeReasonResource,
          data: updatedReasons,
        },
      });

      return updatedReasons;
    },

    async archiveDisputeReason(reason: DisputeReason) {
      const response = await stuut.disputeReasons.archive(reason?.id, reason);
      return response;
    },

    async completeDisputeTask(formData, task: Task, args?) {
      const validation = await this.validateFormData(formData);

      if (validation?.isInvalid) {
        throw new Error(validation.message);
      }

      const { cleanParams } = this.serializeParams(formData, {
        ...args,
        activate: task?.isDisputeActivate,
        create: task?.isDisputeCreate,
        update: task?.isDisputeUpdate,
      });

      const response = await stuut.tasks.completeWithAction(task, cleanParams);

      await this.reloadDispute();

      return response?.data;
    },

    filterParams(params: Record<string, any>) {
      if (!params) return;

      const isEmptyVal = (val: any) => val === '' || val === null || val === undefined;
      const isAllowedVal = [0, '0'];

      return Object.fromEntries(
        Object.entries(params).filter(([, val]) => isAllowedVal.includes(val) || !isEmptyVal(val)),
      );
    },

    async loadConversations(id: string, args?: ResourceArgs, dispute?: Dispute) {
      if (this.disputeConversationsResource.loading && !args?.force) {
        return this.disputeConversationsResource.request;
      }

      this.disputeConversationsResource.loading = true;
      this.disputeConversationsResource.error = null;

      try {
        this.disputeConversationsResource.request = stuut.disputes.communications(id, {
          params: args?.params,
        });

        const response = await this.disputeConversationsResource.request;

        this.disputeConversationsResource.data = response;
        this.disputeConversationsResource.error = null;
        this.disputeConversationsResource.loadedAt = Date.now();

        return response;
      } catch (error) {
        this.disputeConversationsResource.error = error as HttpError;
        throw error;
      } finally {
        this.disputeConversationsResource.request = undefined;
        this.disputeConversationsResource.loading = false;
      }
    },

    async loadDispute(id: string, args?: ResourceArgs) {
      if (this.disputeResource.loading && !args?.force) {
        return this.disputeResource.request;
      }

      this.disputeResource.loading = true;
      this.disputeResource.error = null;

      try {
        this.disputeResource.request = stuut.disputes.retrieve(id);

        const data = await this.disputeResource.request;

        this.disputeResource.data = data;
        this.disputeResource.error = null;
        this.disputeResource.loadedAt = Date.now();
      } catch (error) {
        this.disputeResource.error = error as HttpError;
        throw error;
      } finally {
        this.disputeResource.request = undefined;
        this.disputeResource.loading = false;
      }
    },

    async loadDisputeTasks() {
      if (!this.disputeResource.data?.id) return;

      if (this.disputeTasksResource.loading) {
        return this.disputeTasksResource.request;
      }

      this.disputeTasksResource.loading = true;
      this.disputeTasksResource.error = null;

      try {
        const params = {
          dispute: this.disputeResource.data?.id,
          is_subtask_view: true,
        };

        this.disputeTasksResource.request = stuut.tasks.list({ params });

        const data = await this.disputeTasksResource.request;

        this.disputeTasksResource.data = data;
        this.disputeTasksResource.error = null;
        this.disputeTasksResource.loadedAt = Date.now();
      } catch (error) {
        this.disputeTasksResource.error = error as HttpError;
        throw error;
      } finally {
        this.disputeTasksResource.request = undefined;
        this.disputeTasksResource.loading = false;
      }
    },

    async loadReasons(args?: ResourceArgs) {
      if (this.disputeReasonResource.loading && !args?.force) {
        return this.disputeReasonResource.request;
      }

      this.disputeReasonResource.loading = true;
      this.disputeReasonResource.error = null;

      try {
        this.disputeReasonResource.request = stuut.disputeReasons.list({
          params: args?.params,
        });

        const response = await this.disputeReasonResource.request;

        this.disputeReasonResource.data = response;
        this.disputeReasonResource.error = null;
        this.disputeReasonResource.loadedAt = Date.now();

        return response;
      } catch (error) {
        this.disputeReasonResource.error = error as HttpError;
        throw error;
      } finally {
        this.disputeReasonResource.request = undefined;
        this.disputeReasonResource.loading = false;
      }
    },

    async loadSuggestedReasons() {
      if (this.suggestedReasonResource.loading) {
        return this.suggestedReasonResource.request;
      }

      this.suggestedReasonResource.loading = true;
      this.suggestedReasonResource.error = null;

      try {
        this.suggestedReasonResource.request = stuut.disputeReasons
          .suggested()
          .then((r) => r.data as SuggestedDisputeReason[]);

        const response = await this.suggestedReasonResource.request;

        this.suggestedReasonResource.data = response;
        this.suggestedReasonResource.error = null;
        this.suggestedReasonResource.loadedAt = Date.now();
      } catch (error) {
        this.suggestedReasonResource.error = error as HttpError;
        throw error;
      } finally {
        this.suggestedReasonResource.request = undefined;
        this.suggestedReasonResource.loading = false;
      }

      return this.suggestedReasonResource.data as SuggestedDisputeReason[];
    },

    async reloadDispute(args?: ResourceArgs) {
      if (!this.disputeResource.data?.id) return;
      return this.loadDispute(this.disputeResource.data?.id, { force: args?.force });
    },

    serializeParams(formData, args) {
      const params = { ...formData?.values };

      if (args?.update || args?.activate) {
        if (params.invoice) delete params.invoice;
        if (params.partner) delete params.partner;
      } else {
        params.items = [
          {
            amount: formData?.values?.amount,
            invoice: formData?.values?.invoice,
          },
        ];

        if (params.invoice) delete params.invoice;
      }

      return {
        cleanParams: this.filterParams(params),
        formData,
        params,
      };
    },

    updateDisputePage() {
      this.reloadDispute();
      this.loadDisputeTasks();
    },

    async validateFormData(formData) {
      const result = await formData?.validate();
      const invalidFields = Object.keys(result?.results || {}).filter(
        (key) => result?.results?.[key]?.valid === false,
      );

      return {
        invalidFields,
        invalidFieldStr: invalidFields.join(', '),
        isInvalid: !result?.valid,
        isValid: result?.valid,
        message: `Fix these fields and retry: ${invalidFields.join(', ')}`,
        result,
      };
    },
  },

  getters: {
    activeReasons: (state): DisputeReason[] =>
      state.disputeReasonResource?.data?.filter((r) => r.is_active) || [],
    allSuggestedReasons: (state): SuggestedDisputeReason[] | undefined =>
      state.suggestedReasonResource?.data,
    dispute: (state): Dispute | undefined => state.disputeResource?.data,
    disputeConversations: (state): PaginatedResult<Conversation> | undefined =>
      state.disputeConversationsResource?.data,
    disputeReasonCodes: (state): string[] | undefined =>
      state.disputeReasonResource?.data?.map((re) => re.code),
    disputeTasks: (state): Task[] | undefined => state.disputeTasksResource?.data,
    getById: (state): ((id: string) => Dispute | undefined) => {
      return (id: string) => state.disputes.find((dispute) => dispute?.id === id);
    },

    getReasonById: (state): ((id: string) => DisputeReason | undefined) => {
      return (id: string) => state.disputeReasonResource?.data?.find((reason) => reason?.id === id);
    },

    isDisputesEnabled: (): boolean | undefined => {
      return useProfile()?.isDisputesEnabled;
    },

    reasons: (state): DisputeReason[] => state.disputeReasonResource?.data || [],

    suggestedReasons(state): SuggestedDisputeReason[] | undefined {
      if (!state.suggestedReasonResource?.data?.length) return;

      const reasonCodes =
        state.disputeReasonResource?.data?.map((re) => re.code?.toLowerCase()) || [];
      return state.suggestedReasonResource?.data?.filter(
        (re) => !reasonCodes.includes(re.code?.toLowerCase()),
      );
    },
  },

  state: (): State => ({
    disputeConversationsResource: createResource<PaginatedResult<Conversation>>(),
    disputeReasonResource: createResource<DisputeReason[]>(),
    disputeResource: createResource<Dispute>(),
    disputes: [],
    disputeTasksResource: createResource<Task[]>(),
    suggestedReasonResource: createResource<SuggestedDisputeReason[]>(),
  }),
});

export default useDisputeStore;
